/**
 * Settings > Team > Invitations and the invite window (owner 2026-10-25, branch claude/team-onboarding).
 * The server (api/user-invitations/list) sends sentAt / openedAt / expiresAt and a status.
 */

export type InviteStatus = "sent" | "opened" | "expired" | "accepted";

export type TeamInvitation = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  userRoleEnumId?: string | null;
  userRole?: string | null;
  accessLabel: string;
  sentAt: string;
  openedAt?: string | null;
  expiresAt: string;
  status: InviteStatus;
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "5 minutes ago", "2 hours ago", "yesterday", "3 days ago", "Oct 9". */
export const timeAgo = (iso: string, now: Date = new Date()) => {
  const then = new Date(iso);
  const diff = now.getTime() - then.getTime();
  if (Number.isNaN(diff)) return "";
  if (diff < 2 * MINUTE) return "just now";
  if (diff < HOUR) return `${Math.round(diff / MINUTE)} minutes ago`;
  if (diff < 2 * HOUR) return "1 hour ago";
  if (diff < DAY) return `${Math.round(diff / HOUR)} hours ago`;
  if (diff < 2 * DAY) return "yesterday";
  if (diff < 14 * DAY) return `${Math.floor(diff / DAY)} days ago`;
  return shortDay(iso);
};

export const shortDay = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export type StatusPill = {
  text: string;
  tone: "blue" | "green" | "amber" | "red";
};

/** The Status column: what happened and when. */
export const inviteStatusPill = (
  invite: Pick<TeamInvitation, "status" | "sentAt" | "openedAt" | "expiresAt">,
  now: Date = new Date(),
): StatusPill => {
  if (invite.status === "expired")
    return { text: `Expired · ${shortDay(invite.expiresAt)}`, tone: "red" };
  if (invite.status === "opened" && invite.openedAt)
    return { text: `Opened ${timeAgo(invite.openedAt, now)}`, tone: "green" };
  const sent = timeAgo(invite.sentAt, now);
  // Two days without a visit: worth a nudge.
  if (now.getTime() - new Date(invite.sentAt).getTime() >= 2 * DAY)
    return { text: `Not opened · sent ${sent}`, tone: "amber" };
  return { text: `Sent ${sent}`, tone: "blue" };
};

export const inviteName = (
  invite: Pick<TeamInvitation, "firstName" | "lastName" | "email">,
) =>
  `${invite.firstName || ""} ${invite.lastName || ""}`.trim() ||
  invite.email ||
  "";

export const inviteLink = (origin: string, id: string) =>
  `${origin.replace(/\/+$/, "")}/user-invitations/${id}`;

export const initialsOf = (first?: string | null, last?: string | null) =>
  `${(first || "").trim().charAt(0)}${(last || "")
    .trim()
    .charAt(0)}`.toUpperCase();

/** Plain names for the permission switches (the server's names stay underneath). */
const PERMISSION_LABELS: Record<string, string> = {
  "view settings": "Settings",
  "view firmswork": "Whole firm's work",
  "view firm's work": "Whole firm's work",
  "view billings": "Billing",
  "view billing": "Billing",
  "view reports": "Reports",
  "view activity log": "Activity log",
  "send from any connected email": "Send from any connected email",
};

export const permissionLabel = (name: string) =>
  PERMISSION_LABELS[(name || "").trim().toLowerCase()] || name;

/** Short help under each switch in the invite window. */
const PERMISSION_SHORT_HELP: Record<string, string> = {
  "view settings": "Change firm settings, team, templates and work types.",
  "view firmswork": "See everyone's work, not just their own.",
  "view firm's work": "See everyone's work, not just their own.",
  "view billings": "Invoices, payments and billing reports.",
  "view billing": "Invoices, payments and billing reports.",
  "view reports": "Open the Reports section.",
  "view activity log": "See the firm-wide history of changes.",
  "manage subscription & payment":
    "The firm's ProCharted plan, seats, bills and card. Not automatic for Admins.",
};

export const permissionShortHelp = (name: string) =>
  PERMISSION_SHORT_HELP[(name || "").trim().toLowerCase()] || "";

/** The access level cards: name shown + one line, in the server's level names. */
export const ACCESS_LEVEL_CARDS: {
  level: string;
  title: string;
  text: string;
}[] = [
  {
    level: "Admin",
    title: "Admin",
    text: "Everything, including Settings and Billing.",
  },
  {
    level: "Staff (No Settings)",
    title: "Staff",
    text: "All work and billing. No Settings.",
  },
  {
    level: "Staff (No Settings, No Billing)",
    title: "Staff, no billing",
    text: "All work. No Settings or Billing.",
  },
  { level: "Custom", title: "Custom", text: "Choose each permission below." },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type InviteForm = { firstName: string; lastName: string; email: string };

/** Problems per field (empty object = ready to send). */
export const inviteFormProblems = (form: InviteForm) => {
  const problems: Partial<Record<keyof InviteForm, string>> = {};
  if (!form.firstName.trim()) problems.firstName = "Enter a first name.";
  if (!form.lastName.trim()) problems.lastName = "Enter a last name.";
  if (!form.email.trim()) problems.email = "Enter an email address.";
  else if (!EMAIL_RE.test(form.email.trim()))
    problems.email = "Enter a valid email address.";
  return problems;
};
